
import { useState } from 'react';

const solarFeatures = [
  {
    icon: "ri-shield-check-line",
    title: "25-Year Performance Warranty",
    desc: "Industry-leading warranty guarantees at least 85% power output for a full 25 years.",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "30% Federal Tax Credit",
    desc: "Take advantage of the federal Investment Tax Credit and save thousands on installation.",
  },
  {
    icon: "ri-flashlight-line",
    title: "Tier-1 Premium Panels",
    desc: "We exclusively install top-rated monocrystalline panels with 22%+ efficiency ratings.",
  },
  {
    icon: "ri-tools-line",
    title: "Professional Roof Mounting",
    desc: "Certified installers use engineered racking systems designed for Florida hurricane codes.",
  },
];

const solarGallery = [
  {
    src: "https://public.readdy.ai/ai/img_res/76226cfe7842080230801d41763c23df.jpg",
    alt: "Premium monocrystalline solar panels close-up",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Professional%20photograph%20of%20two%20solar%20installation%20technicians%20in%20hard%20hats%20and%20safety%20gear%20carefully%20mounting%20solar%20panels%20on%20a%20residential%20rooftop%20in%20Florida%2C%20bright%20sunny%20day%2C%20organized%20tools%20and%20equipment%20visible%2C%20professional%20teamwork%20clean%20white%20stucco%20home%20below%2C%20commercial%20photography%20with%20warm%20natural%20lighting&width=600&height=400&seq=solar-gallery-install-002&orientation=landscape",
    alt: "Professional solar panel installation team",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Beautiful%20wide%20angle%20photograph%20of%20a%20completed%20residential%20solar%20panel%20array%20on%20a%20large%20Florida%20home%20roof%2C%20perfectly%20aligned%20rows%20of%20black%20panels%2C%20stunning%20sunset%20golden%20hour%20lighting%20palm%20trees%20silhouetted%20in%20background%2C%20pristine%20tile%20roof%2C%20professional%20real%20estate%20photography%20showing%20full%20system%20installation&width=600&height=400&seq=solar-gallery-complete-003&orientation=landscape",
    alt: "Completed residential solar installation",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Professional%20photograph%20of%20a%20modern%20home%20solar%20energy%20monitoring%20system%20display%20showing%20real-time%20power%20generation%20data%2C%20sleek%20digital%20interface%20on%20a%20tablet%20mounted%20on%20wall%2C%20graphs%20showing%20energy%20production%20and%20savings%2C%20clean%20modern%20interior%20background%2C%20bright%20natural%20lighting%20technology%20product%20photography%20style&width=600&height=400&seq=solar-gallery-monitor-004&orientation=landscape",
    alt: "Solar energy monitoring system",
  },
];

export default function SolarSection() {
  const [activeImage, setActiveImage] = useState(0);

  // Guard against out‑of‑range index (e.g., if the gallery array becomes empty)
  const safeIndex = Math.min(Math.max(activeImage, 0), solarGallery.length - 1);
  const { src, alt } = solarGallery[safeIndex] || {};

  return (
    <section id="solar-installation" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row gap-16 items-center mb-20">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-brand-gold-light rounded-lg">
                <i className="ri-sun-line text-brand-gold text-xl"></i>
              </div>
              <span className="text-sm font-bold text-brand-gold tracking-wider uppercase">
                Solar Installation
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Harness Florida&apos;s Sunshine With Premium Solar Panels
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Florida averages 237 sunny days per year — making it one of the best states for
              solar energy. Our premium tier-1 monocrystalline panels convert more sunlight into
              electricity, delivering up to 70% reduction in your monthly energy bills. Every system
              is custom-designed for your roof, engineered to withstand Category 5 hurricanes, and
              backed by a 25-year performance warranty.
            </p>

            <div className="grid grid-cols-3 gap-6 mb-8">
              <div className="bg-brand-cream rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-brand-navy">70%</p>
                <p className="text-sm text-gray-600 mt-1">Bill Reduction</p>
              </div>
              <div className="bg-brand-cream rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-brand-navy">22%+</p>
                <p className="text-sm text-gray-600 mt-1">Panel Efficiency</p>
              </div>
              <div className="bg-brand-cream rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-brand-navy">$0</p>
                <p className="text-sm text-gray-600 mt-1">Down Payment</p>
              </div>
            </div>
          </div>

          <div className="flex-1 w-full">
            <div className="rounded-3xl overflow-hidden shadow-2xl mb-4">
              {src ? (
                <img
                  src={src}
                  alt={alt}
                  className="w-full h-[400px] object-cover object-top transition-all duration-500"
                />
              ) : (
                <div className="w-full h-[400px] flex items-center justify-center bg-gray-100 text-gray-500">
                  No image available
                </div>
              )}
            </div>
            <div className="grid grid-cols-4 gap-3">
              {solarGallery.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImage(i)}
                  className={`rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
                    activeImage === i
                      ? "ring-3 ring-brand-red scale-105"
                      : "opacity-70 hover:opacity-100"
                  }`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-[80px] object-cover object-top"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {solarFeatures.map((feature) => (
            <div
              key={feature.title}
              className="bg-brand-cream rounded-2xl p-6 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-brand-gold-light rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${feature.icon} text-brand-gold text-2xl`}></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
